import React from 'react';
import { Scale, ActivitySquare, Droplet, Dumbbell, Activity, TrendingDown, TrendingUp } from 'lucide-react';
import BodyCompositionChart from './BodyCompositionChart';

const extractVal = (val) => {
  if (val === null || val === undefined) return null;
  if (typeof val === 'object') return val.value ?? val.val ?? val.current ?? null;
  return val;
};

export default function UnifiedBodyCompositionCard({ healthData, healthSourceLabel }) {
  if (!healthData) return null;

  const weight = healthData?.weight || {};
  const bodyFat = healthData?.bodyFat || {};
  const hydration = healthData?.hydration || {};
  
  const wCurrent = extractVal(weight.current);
  const wPrevious = extractVal(weight.previous);
  const fCurrent = extractVal(bodyFat.current);
  const fPrevious = extractVal(bodyFat.previous);
  
  const weightTrend = wCurrent != null && wPrevious != null && wCurrent < wPrevious ? 'down' : 'up';
  const fatTrend = fCurrent != null && fPrevious != null && fCurrent < fPrevious ? 'down' : 'up';
  
  const compData = healthData?.composition || {};
  const derivedMuscle = wCurrent ? parseFloat((wCurrent * 0.8).toFixed(1)) : null;
  const derivedBone = wCurrent ? parseFloat((wCurrent * 0.04).toFixed(1)) : null;
  const waterPct = extractVal(compData?.waterPct) ?? null;
  const muscleMass = extractVal(compData?.muscleMass) ?? derivedMuscle;
  const boneMass = extractVal(compData?.boneMass) ?? derivedBone;
  const spo2Val = extractVal(healthData.spo2?.current);

  return (
    <div className="glass-panel" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 className="text-muted text-sm text-gradient-health" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600 }}>
          <Activity size={16} /> Composición Corporal Integral
        </h3>
        <span style={{ fontSize: '0.65rem', background: 'rgba(0,229,255,0.15)', color: '#00e5ff', padding: '2px 8px', borderRadius: '12px', fontWeight: 600 }}>
          {healthSourceLabel || 'Sin sincronizar'}
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
        
        {/* PESO */}
        <div style={{ background: 'rgba(255,255,255,0.03)', padding: '1rem', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <span className="text-xs text-muted" style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 600, textTransform: 'uppercase' }}><Scale size={12} color="#00e5ff" /> Peso</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem' }}>
            <span style={{ fontSize: '2rem', fontWeight: 800, color: 'white', lineHeight: 1 }}>{wCurrent != null ? wCurrent : '--'}</span>
            <span style={{ fontSize: '0.85rem', color: '#00e5ff', fontWeight: 600 }}>kg</span>
            <span className={`trend-pill ${weightTrend}`} style={{ marginLeft: 'auto', fontSize: '0.65rem' }}>
              {weightTrend === 'down' ? <TrendingDown size={10} /> : <TrendingUp size={10} />}
              {wCurrent != null && wPrevious != null ? Math.abs(wCurrent - wPrevious).toFixed(1) : '--'}
            </span>
          </div>
        </div>

        {/* GRASA */}
        <div style={{ background: 'rgba(255,255,255,0.03)', padding: '1rem', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <span className="text-xs text-muted" style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 600, textTransform: 'uppercase' }}><ActivitySquare size={12} color="#8b5cf6" /> Índice Grasa</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem' }}>
            <span style={{ fontSize: '2rem', fontWeight: 800, color: 'white', lineHeight: 1 }}>{fCurrent != null ? fCurrent : '--'}</span>
            <span style={{ fontSize: '0.85rem', color: '#8b5cf6', fontWeight: 600 }}>%</span>
            <span className={`trend-pill ${fatTrend}`} style={{ marginLeft: 'auto', fontSize: '0.65rem' }}>
              {fatTrend === 'down' ? <TrendingDown size={10} /> : <TrendingUp size={10} />}
              {fCurrent != null && fPrevious != null ? Math.abs(fCurrent - fPrevious).toFixed(1) : '--'}
            </span>
          </div>
          <div style={{ fontSize: '0.7rem', color: 'rgba(139,92,246,0.8)', fontWeight: 600, marginTop: '0.5rem' }}>Objetivo: {extractVal(bodyFat.goal) || '--'}%</div>
        </div>

      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
        
        {/* AGUA */}
        <div style={{ background: 'rgba(255,255,255,0.03)', padding: '1rem', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <span className="text-xs text-muted" style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 600, textTransform: 'uppercase' }}><Droplet size={12} color="#38bdf8" /> Agua Corp.</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem' }}>
            <span style={{ fontSize: '1.5rem', fontWeight: 700, color: 'white', lineHeight: 1 }}>{waterPct != null ? Number(waterPct).toFixed(1) : '--'}</span>
            <span style={{ fontSize: '0.8rem', color: '#38bdf8', fontWeight: 600 }}>%</span>
            <span style={{ fontSize: '0.7rem', marginLeft: 'auto', color: waterPct != null && waterPct >= 50 && waterPct <= 65 ? 'var(--health-green)' : (waterPct != null ? '#f59e0b' : 'var(--text-secondary)'), fontWeight: 600 }}>
              {waterPct != null ? (waterPct >= 50 && waterPct <= 65 ? 'Óptima' : (waterPct < 50 ? 'Baja' : 'Alta')) : '--'}
            </span>
          </div>
          <div style={{ width: '100%', height: '6px', background: 'rgba(255,255,255,0.06)', borderRadius: '3px', overflow: 'hidden', marginTop: '0.6rem' }}>
            <div style={{ height: '100%', width: `${waterPct != null ? Math.min((waterPct / 75) * 100, 100) : 0}%`, background: 'linear-gradient(90deg, #38bdf8, var(--health-cyan))' }} />
          </div>
          <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)', marginTop: '0.4rem' }}>
            {extractVal(hydration?.currentLiters) != null ? `${extractVal(hydration?.currentLiters)}L bebido hoy` : 'Sin registros de hidratación'}
          </div>
        </div>

        {/* MASA MUSCULAR / ÓSEA */}
        <div style={{ background: 'rgba(255,255,255,0.03)', padding: '1rem', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '0.8rem' }}>
          <span className="text-xs text-muted" style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 600, textTransform: 'uppercase', marginBottom: '0.2rem' }}>
            <Dumbbell size={12} color="#a78bfa" /> Masa Muscular / Ósea
          </span>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="text-xs text-muted" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 500 }}>Muscular</span>
            <span style={{ fontSize: '1rem', fontWeight: 700, color: 'white' }}>{muscleMass != null ? muscleMass : '--'} <span style={{fontSize:'0.7rem', color:'rgba(255,255,255,0.5)'}}>kg</span></span>
          </div>
          <div style={{ height: '1px', background: 'rgba(255,255,255,0.1)' }}></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="text-xs text-muted" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 500 }}><Scale size={14} color="#fb923c" /> Ósea</span>
            <span style={{ fontSize: '1rem', fontWeight: 700, color: 'white' }}>{boneMass != null ? boneMass : '--'} <span style={{fontSize:'0.7rem', color:'rgba(255,255,255,0.5)'}}>kg</span></span>
          </div>
        </div>

        {/* OXÍGENO EN SANGRE (SpO2) */}
        <div style={{ background: 'rgba(255,255,255,0.03)', padding: '1rem', borderRadius: '12px', border: `1px solid ${spo2Val == null ? 'rgba(255,255,255,0.05)' : spo2Val >= 94 ? 'rgba(255,255,255,0.05)' : spo2Val >= 91 ? 'rgba(245,158,11,0.2)' : 'rgba(239,68,68,0.2)'}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <span className="text-xs text-muted" style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 600, textTransform: 'uppercase' }}>
              <Activity size={12} color={spo2Val == null ? '#94a3b8' : spo2Val >= 94 ? '#10b981' : spo2Val >= 91 ? '#f59e0b' : '#ef4444'} /> SpO2
            </span>
            <span style={{ fontSize: '0.7rem', color: spo2Val == null ? '#64748b' : spo2Val >= 94 ? '#10b981' : spo2Val >= 91 ? '#f59e0b' : '#ef4444', fontWeight: 700 }}>
              {spo2Val == null ? 'Sin datos' : spo2Val >= 94 ? '✓ Normal' : spo2Val >= 91 ? '⚡ Leve variación' : '⚠️ Hipoxia'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem' }}>
            <span style={{ fontSize: '2rem', fontWeight: 800, color: 'white', lineHeight: 1 }}>{spo2Val != null ? spo2Val : '--'}</span>
            <span style={{ fontSize: '0.85rem', color: spo2Val == null ? '#64748b' : spo2Val >= 94 ? '#10b981' : spo2Val >= 91 ? '#f59e0b' : '#ef4444', fontWeight: 600 }}>%</span>
          </div>
          <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)', marginTop: '0.5rem', fontWeight: 500 }}>
            {spo2Val == null ? 'Sincroniza tu reloj para obtener SpO2' : spo2Val >= 94 ? 'Buena oxigenación del SNC' : spo2Val >= 91 ? 'Fluctuación nocturna normal' : 'Atención a la altitud o fatiga'}
          </div>
        </div>

      </div>

      <BodyCompositionChart 
        weightHistory={weight?.history && weight.history.length > 0 ? weight.history : (wCurrent != null ? [{ date: new Date().toISOString().split('T')[0], value: wCurrent }] : [])} 
        fatHistory={bodyFat?.history && bodyFat.history.length > 0 ? bodyFat.history : (fCurrent != null ? [{ date: new Date().toISOString().split('T')[0], value: fCurrent }] : [])} 
      />
    </div>
  );
}
